
import { AIEvent, AppConfig, Comment } from "../types";
import { supabaseAuth } from "../lib/supabase";

export const supabaseService = {
  isConfigured(config: AppConfig): boolean {
    return !!(config.supabaseUrl && config.supabaseAnonKey && config.supabaseTableName);
  },

  async getHeaders(config: AppConfig) {
    const token = await supabaseAuth.getAccessToken();
    return {
      'apikey': config.supabaseAnonKey!.trim(),
      'Authorization': `Bearer ${token || config.supabaseAnonKey!.trim()}`,
      'Content-Type': 'application/json'
    };
  },

  async testConnection(config: AppConfig): Promise<{ success: boolean; message: string; rlsIssue?: boolean }> {
    if (!this.isConfigured(config)) {
      return { success: false, message: "Missing configuration fields." };
    }

    try {
      const headers = await this.getHeaders(config);
      const url = `${config.supabaseUrl!.trim()}/rest/v1/${encodeURIComponent(config.supabaseTableName!.trim())}?select=count`;
      const response = await fetch(url, {
        method: 'GET',
        headers: { ...headers, 'Range': '0-0' }
      });

      if (response.ok) {
        return { success: true, message: "Connected to Supabase!" };
      } else {
        const err = await response.json().catch(() => ({ message: response.statusText }));
        if (response.status === 401) return { success: false, message: "Access Denied (401). Check API Key.", rlsIssue: true };
        if (response.status === 403) return { success: false, message: "Forbidden (403). Check RLS Policies.", rlsIssue: true };
        if (response.status === 404) return { success: false, message: `Table '${config.supabaseTableName}' not found. Check Admin Settings.`, rlsIssue: false };
        return { success: false, message: err.message || `Error: ${response.status}` };
      }
    } catch (err: any) {
      console.error("Supabase Connection Test Failed:", err);
      return { success: false, message: "Network Error: Unable to reach Supabase. Check your URL and connection." };
    }
  },

  getValue(row: any, ...keys: string[]): any {
    if (!row) return undefined;
    const rowKeys = Object.keys(row);
    for (const key of keys) {
      if (row[key] !== undefined) return row[key];
      // Search for key case-insensitively and ignoring underscores
      const foundKey = rowKeys.find(rk => {
        const normalizedRK = rk.toLowerCase().replace(/_/g, '').replace(/\s/g, '');
        const normalizedKey = key.toLowerCase().replace(/_/g, '').replace(/\s/g, '');
        return normalizedRK === normalizedKey;
      });
      if (foundKey) return row[foundKey];
    }
    return undefined;
  },

  mapFromDb(row: any): AIEvent {
    return {
      id: (this.getValue(row, "id", "ID") || "").toString(),
      campaignName: this.getValue(row, "campaign_name", "campaignName", "Campaign Name") || "Untitled Event",
      category: this.getValue(row, "category", "Category") || "General",
      url: this.getValue(row, "url", "URL") || "",
      priority: this.getValue(row, "priority") || "",
      year: parseInt(this.getValue(row, "year", "Year")) || new Date().getFullYear(),
      month: parseInt(this.getValue(row, "month", "Month")) || (new Date().getMonth() + 1),
      startDate: this.getValue(row, "start_date", "startDate", "Start Date") || "",
      endDate: this.getValue(row, "end_date", "endDate", "End Date") || "",
      location: this.getValue(row, "location", "Location") || "Unknown",
      focusArea: this.getValue(row, "focus_area", "focusArea", "Focus Area") || "",
      information: this.getValue(row, "information", "Information") || "",
      addedAt: this.getValue(row, "added_at", "addedAt", "Added At") || new Date().toISOString(),
      attendeeSize: this.getValue(row, "attendee_size", "attendeeSize", "Audience Size") || "",
      targetAudience: this.getValue(row, "target_audience", "targetAudience", "Target Audience") || ""
    };
  },

  mapToDb(event: AIEvent): any {
    return {
      "id": event.id,
      "campaign_name": event.campaignName,
      "category": event.category,
      "url": event.url,
      "priority": event.priority || null,
      "year": event.year,
      "month": event.month,
      "start_date": event.startDate,
      "end_date": event.endDate,
      "location": event.location,
      "focus_area": event.focusArea,
      "information": event.information,
      "added_at": event.addedAt,
      "attendee_size": event.attendeeSize || null,
      "target_audience": event.targetAudience || null
    };
  },

  async fetchEvents(config: AppConfig): Promise<AIEvent[]> {
    if (!this.isConfigured(config)) return [];
    const tableName = config.supabaseTableName!.trim();
    const baseUrl = config.supabaseUrl!.trim();
    
    try {
      const headers = await this.getHeaders(config);
      const url = `${baseUrl}/rest/v1/${encodeURIComponent(tableName)}?select=*&order=start_date.asc`;
      
      const response = await fetch(url, { headers });
      
      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({ message: response.statusText }));
        
        if (response.status === 404) {
           throw new Error(`Table Not Found (404): The table '${tableName}' does not exist in your Supabase database. Please check Admin Settings > Events Table.`);
        }
        if (response.status === 406) throw new Error("Schema Mismatch (406): Table columns don't match the expected Pulse schema.");
        if (response.status === 401 || response.status === 403) {
          throw new Error(`Authentication/RLS Issue (${response.status}): Your account is logged in but has no permission to read this table.`);
        }
        throw new Error(`Supabase Error ${response.status}: ${errorBody.message || response.statusText}`);
      }
      
      const data = await response.json();
      return Array.isArray(data) ? data.map((row: any) => this.mapFromDb(row)) : [];
    } catch (err: any) {
      console.error("Supabase Fetch Failed:", err);
      if (err instanceof TypeError || err.message === 'Failed to fetch' || err.name === 'TypeError') {
         throw new Error("Unable to connect to database. Please verify your Supabase URL and internet connection.");
      }
      throw err;
    }
  },

  async insertEvent(config: AppConfig, event: AIEvent) {
    if (!this.isConfigured(config)) return;
    try {
      const headers = await this.getHeaders(config);
      const response = await fetch(`${config.supabaseUrl!.trim()}/rest/v1/${encodeURIComponent(config.supabaseTableName!.trim())}`, {
        method: 'POST',
        headers: { ...headers, 'Prefer': 'return=minimal' },
        body: JSON.stringify(this.mapToDb(event))
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ message: `Server error: ${response.status}` }));
        throw new Error(err.message || `Database Write Failed (Status ${response.status}).`);
      }
    } catch (err: any) {
      if (err instanceof TypeError || err.message === 'Failed to fetch') {
        throw new Error("Network Error: Unable to save record. Check your connection.");
      }
      throw err;
    }
  },

  async updateEvent(config: AppConfig, event: AIEvent) {
    if (!this.isConfigured(config)) return;
    try {
      const headers = await this.getHeaders(config);
      const dbRow = this.mapToDb(event);
      delete dbRow["id"]; 
      
      const response = await fetch(`${config.supabaseUrl!.trim()}/rest/v1/${encodeURIComponent(config.supabaseTableName!.trim())}?id=eq.${encodeURIComponent(event.id)}`, {
        method: 'PATCH',
        headers: { ...headers, 'Prefer': 'return=minimal' },
        body: JSON.stringify(dbRow)
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ message: `Server error: ${response.status}` }));
        throw new Error(err.message || `Update Failed (Status ${response.status}).`);
      }
    } catch (err: any) {
      if (err instanceof TypeError || err.message === 'Failed to fetch') {
        throw new Error("Network Error: Unable to update record. Check your connection.");
      }
      throw err;
    }
  },

  async deleteEvent(config: AppConfig, eventId: string) {
    if (!this.isConfigured(config)) return;
    try {
      const headers = await this.getHeaders(config);
      const response = await fetch(`${config.supabaseUrl!.trim()}/rest/v1/${encodeURIComponent(config.supabaseTableName!.trim())}?id=eq.${encodeURIComponent(eventId)}`, {
        method: 'DELETE',
        headers: { ...headers, 'Prefer': 'return=minimal' }
      });

      if (!response.ok) {
        throw new Error(`Delete Failed (Status ${response.status}). Check RLS.`);
      }
    } catch (err: any) {
      if (err instanceof TypeError || err.message === 'Failed to fetch') {
        throw new Error("Network Error: Unable to delete record. Check your connection.");
      }
      throw err;
    }
  },

  // --- COMMENT METHODS ---

  async fetchComments(config: AppConfig, eventId: string): Promise<Comment[]> {
    if (!this.isConfigured(config) || !config.supabaseCommentsTableName) return [];
    
    const tableName = config.supabaseCommentsTableName.trim();
    const baseUrl = config.supabaseUrl!.trim();

    try {
      const headers = await this.getHeaders(config);
      const url = `${baseUrl}/rest/v1/${encodeURIComponent(tableName)}?select=*&event_id=eq.${encodeURIComponent(eventId)}&order=created_at.asc`;
      
      const response = await fetch(url, { headers });
      if (!response.ok) throw new Error("Failed to fetch comments");
      
      const data = await response.json();
      return data.map((row: any) => ({
        id: row.id,
        eventId: row.event_id,
        authorEmail: row.author_email,
        text: row.text,
        timestamp: row.created_at
      }));
    } catch (err) {
      console.error("Fetch comments failed:", err);
      return [];
    }
  },

  async insertComment(config: AppConfig, comment: Comment) {
    if (!this.isConfigured(config) || !config.supabaseCommentsTableName) return;
    
    const headers = await this.getHeaders(config);
    const tableName = config.supabaseCommentsTableName.trim();
    
    const dbRow = {
      id: comment.id,
      event_id: comment.eventId,
      author_email: comment.authorEmail,
      text: comment.text,
      created_at: comment.timestamp
    };

    const response = await fetch(`${config.supabaseUrl!.trim()}/rest/v1/${encodeURIComponent(tableName)}`, {
      method: 'POST',
      headers: { ...headers, 'Prefer': 'return=minimal' },
      body: JSON.stringify(dbRow)
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(err.message || "Failed to post comment");
    }
  }
};
